// Where a trip goes: its stops (the cities we actually stay in, in order) and the name
// that follows from them. Connections and flights home are not stops: LGA → ORD → NRT → SGN
// is one stop, Ho Chi Minh City. Used for trip names and the per-city headers on a trip.

import { addDays } from './time.ts';

/** The fields of a booking (saved or just extracted) that stops depend on. */
export interface StopSource {
  type: string;
  start: { date: string; time?: string };
  end?: { date: string; time?: string };
  from?: { code?: string; city?: string; name?: string };
  to?: { code?: string; city?: string; name?: string };
  place?: { city?: string };
}

export interface Home {
  codes: string[];
  cities: string[];
}

export const DEFAULT_HOME_CODES = ['JFK', 'LGA', 'EWR'];
export const DEFAULT_HOME_CITIES = ['new york', 'newark', 'brooklyn'];

export function home(codes?: string[], cities?: string[]): Home {
  return {
    codes: (codes ?? DEFAULT_HOME_CODES).map((c) => c.toUpperCase()),
    cities: (cities ?? DEFAULT_HOME_CITIES).map((c) => c.toLowerCase()),
  };
}

export interface Stop {
  city: string;
  /** Arrival (or check-in) date. */
  start: string;
  /** The next stop's arrival date, or the last date of the trip. */
  end: string;
}

export const isTransit = (s: { type: string }) => s.type === 'flight' || s.type === 'train';

function isHome(h: Home, code?: string, city?: string): boolean {
  return !!((code && h.codes.includes(code.toUpperCase())) || (city && h.cities.includes(city.toLowerCase())));
}

/** The city a booking takes us to, unless that's home. */
export function destinationOf(s: StopSource, h: Home): string | undefined {
  if (isTransit(s)) return isHome(h, s.to?.code, s.to?.city) ? undefined : s.to?.city ?? s.to?.name ?? s.to?.code;
  const city = s.place?.city;
  return city && !isHome(h, undefined, city) ? city : undefined;
}

const startKey = (s: StopSource) => `${s.start.date} ${s.start.time ?? ''}`;

export function sortByStart<T extends StopSource>(segs: T[]): T[] {
  return [...segs].sort((a, b) => startKey(a).localeCompare(startKey(b)));
}

/**
 * A flight or train is a connection when another one leaves from where it arrived by the
 * next day.
 */
export function isConnection(leg: StopSource, all: StopSource[]): boolean {
  const arrived = leg.end?.date ?? leg.start.date;
  const at = leg.to?.code?.toUpperCase();
  const city = leg.to?.city?.toLowerCase();
  return all.some(
    (next) =>
      next !== leg &&
      isTransit(next) &&
      startKey(next) > startKey(leg) &&
      next.start.date <= addDays(arrived, 1) &&
      ((at && next.from?.code?.toUpperCase() === at) || (city && next.from?.city?.toLowerCase() === city)),
  );
}

function lastDate(segs: StopSource[]): string {
  return segs.reduce((max, s) => {
    const d = s.end?.date && s.end.date > s.start.date ? s.end.date : s.start.date;
    return d > max ? d : max;
  }, '');
}

/** The trip's stops in order. Only flights, trains and stays make a stop. */
export function tripStops(segments: StopSource[], h: Home = home()): Stop[] {
  const sorted = sortByStart(segments);
  const stops: Stop[] = [];
  for (const s of sorted) {
    if (!isTransit(s) && s.type !== 'stay') continue;
    if (isTransit(s) && isConnection(s, sorted)) continue;
    const city = destinationOf(s, h);
    if (!city) continue;
    const arrived = isTransit(s) ? s.end?.date ?? s.start.date : s.start.date;
    const last = stops.at(-1);
    if (last && last.city.toLowerCase() === city.toLowerCase()) continue;
    stops.push({ city, start: arrived, end: arrived });
  }
  const end = lastDate(sorted);
  stops.forEach((st, i) => (st.end = stops[i + 1]?.start ?? (end > st.start ? end : st.start)));
  return stops;
}

/** "Lisbon", "Lisbon & Porto", "Lisbon + 2 more"; a city visited twice counts once. */
export function stopsName(stops: Stop[]): string | undefined {
  const cities: string[] = [];
  for (const s of stops) if (!cities.some((c) => c.toLowerCase() === s.city.toLowerCase())) cities.push(s.city);
  if (cities.length === 0) return undefined;
  if (cities.length === 1) return cities[0];
  if (cities.length === 2) return `${cities[0]} & ${cities[1]}`;
  return `${cities[0]} + ${cities.length - 1} more`;
}

/** The automatic name for a trip: its stops, else the first place any booking names. */
export function tripName(segments: StopSource[], h: Home = home()): string | undefined {
  return stopsName(tripStops(segments, h)) ?? sortByStart(segments).map((s) => destinationOf(s, h)).find(Boolean);
}
